import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Building,
  Mail,
  Phone,
  User,
  Calendar,
  AlertTriangle,
  ArrowLeft,
  FileCheck,
  CheckCircle,
  XCircle,
  FileText,
  Clock,
  Shield,
  Activity,
} from "lucide-react";
import { onboardingService, OnboardingClientDetails } from "../../../services/api/onboardingService";
import { toast } from "sonner";
import { Skeleton, Card, Progress, Tooltip, Button } from "antd";

const OnboardingClientDetailsView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<OnboardingClientDetails | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchClientDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await onboardingService.getClientById(id);
      if (response.status === "success" && response.data) {
        setDetails(response.data);
      } else {
        toast.error(response.message || "Failed to load client details");
      }
    } catch (error) {
      console.error("Error loading client onboarding details:", error);
      toast.error("An error occurred while loading client onboarding details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-abel">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton active paragraph={{ rows: 12 }} />
        </div>
      </div>
    );
  }

  if (!details || !details.client) {
    return (
      <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-abel flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertTriangle size={48} className="mx-auto text-amber-500" />
          <h2 className="text-xl font-bold text-gray-900">Client details not found</h2>
          <Button onClick={() => navigate("/onboarding-clients")}>Back to List</Button>
        </div>
      </div>
    );
  }

  const { client, metrics } = details;

  // Calculate approval percentage
  const approvalPercentage =
    metrics.totalApprovalsRequired > 0
      ? Math.round((metrics.approvalsCount / metrics.totalApprovalsRequired) * 100)
      : 0;

  const renderApprovalStatus = (isApproved: boolean, label: string) => (
    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all duration-300">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {isApproved ? (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full">
          <CheckCircle size={12} />
          Approved
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 bg-gray-50 px-2.5 py-0.5 rounded-full">
          <Clock size={12} />
          Pending
        </span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen font-abel bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* Back Button & Main Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <button
            onClick={() => navigate("/onboarding-clients")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Onboardings
          </button>
          
          <div className="flex gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
              client.onboardingStatus === "COMPLETED"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-blue-100 text-blue-800"
            }`}>
              {client.onboardingStatus?.replace(/_/g, " ")}
            </span>
          </div>
        </div>

        {/* Top Info Banner */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-tr from-[#0B60D6] to-[#0d85ee] rounded-xl flex items-center justify-center text-white">
              <Building size={28} />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">{client.name}</h1>
              <p className="text-sm text-gray-500 mt-1">Slug: {client.slug}</p>
            </div>
          </div>
          
          {/* Progress Tracker Widget */}
          <div className="w-full md:w-64 space-y-2">
            <div className="flex justify-between text-xs font-semibold text-gray-500">
              <span>Approvals Completion</span>
              <span>{metrics.approvalsCount}/{metrics.totalApprovalsRequired} Verified</span>
            </div>
            <Progress
              percent={approvalPercentage}
              strokeColor="#0B60D6"
              trailColor="#E5E7EB"
              showInfo={false}
              className="m-0"
            />
          </div>
        </div>

        {/* Client Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Days In Current Step */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
              <Activity size={20} />
            </div>
            <div>
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider block">Days in Current Step</span>
              <span className="text-lg font-bold text-gray-900">{metrics.daysInCurrentStep || 0} days</span>
            </div>
          </div>

          {/* Days Remaining / Overdue */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
            <div className={`p-3 rounded-lg ${metrics.isOverdue ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
              {metrics.isOverdue ? <AlertTriangle size={20} /> : <Calendar size={20} />}
            </div>
            <div>
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider block">Deadline Status</span>
              <span className={`text-lg font-bold ${metrics.isOverdue ? "text-rose-600" : "text-gray-900"}`}>
                {metrics.isOverdue ? "Overdue" : `${metrics.daysRemainingDeadline ?? "No deadline"} days left`}
              </span>
            </div>
          </div>

          {/* Nudge Tracker */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
              <Clock size={20} />
            </div>
            <div>
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider block">Nudges Triggered</span>
              <span className="text-lg font-bold text-gray-900">{client.onboardingNudgeCount || 0} times</span>
            </div>
          </div>

          {/* Last Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
            <div className="p-3 bg-purple-50 text-purple-600 rounded-lg">
              <Activity size={20} />
            </div>
            <div>
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider block">Last Activity</span>
              <span className="text-sm font-semibold text-gray-900">
                {client.lastOnboardingActivityAt ? new Date(client.lastOnboardingActivityAt).toLocaleDateString() : "Never"}
              </span>
            </div>
          </div>
        </div>

        {/* Main Details and Approvals Checklist Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* General details Card */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                <User size={18} className="text-[#0B60D6]" />
                Client Representative Information
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Full Name</span>
                  <span className="text-sm font-medium text-gray-900">
                    {client.representativeFirstName} {client.representativeLastName}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Email Address</span>
                  <a href={`mailto:${client.representativeEmail}`} className="text-sm font-medium text-[#0B60D6] flex items-center gap-1">
                    <Mail size={14} />
                    {client.representativeEmail}
                  </a>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Phone Number</span>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Phone size={14} className="text-gray-400" />
                    {client.contactPhone || "Not provided"}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Role</span>
                  <span className="text-sm font-medium text-gray-900">
                    {client.representativeRole || "Not specified"}
                  </span>
                </div>
              </div>
            </div>

            {/* General Info Card */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                <Building size={18} className="text-[#0B60D6]" />
                Additional Onboarding Milestones
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Resumption Step</span>
                  <span className="text-sm font-semibold text-gray-800 bg-gray-50 border border-gray-100 px-2.5 py-1 rounded-md inline-block">
                    {client.onboardingResumptionStep || "First Step"}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Onboard Deadline Date</span>
                  <span className="text-sm font-medium text-gray-900">
                    {client.onboardingDeadline ? new Date(client.onboardingDeadline).toLocaleDateString() : "Not scheduled"}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Agreement Status</span>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                    client.postOnboardAgreementStatus === "ACCEPTED"
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {client.postOnboardAgreementStatus || "PENDING"}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Nudge Reminders Sent</span>
                  <span className="text-sm font-medium text-gray-900">
                    {client.postOnboardAgreementReminderCount || 0} reminders
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Internal Approvals Checklist */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                <Shield size={18} className="text-[#0B60D6]" />
                Internal Approvals Checklist
              </h2>
              
              <div className="space-y-3">
                {renderApprovalStatus(client.onboardingApprovedByCTO, "CTO Sign Up Approval")}
                {renderApprovalStatus(client.onboardingApprovedByCEO, "CEO Sign Up Approval")}
                {renderApprovalStatus(client.ceoApprovedDocs, "CEO Documents Approval")}
                {renderApprovalStatus(client.goLiveApprovedByIT, "IT Go-Live Approval")}
                {renderApprovalStatus(client.goLiveApprovedByCTO, "CTO Go-Live Approval")}
                {renderApprovalStatus(client.goLiveApprovedByCEO, "CEO Go-Live Approval")}
                {renderApprovalStatus(client.goLiveApprovedByClient, "Client Go-Live Approval")}
              </div>
            </div>

            {/* Documents & PDF links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                <FileText size={18} className="text-[#0B60D6]" />
                Associated Documents
              </h2>
              
              <div className="space-y-3">
                {client.progressReportUrl ? (
                  <a
                    href={client.progressReportUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg border border-[#0B60D6]/20 bg-blue-50/50 hover:bg-blue-50 transition-colors"
                  >
                    <FileText className="text-[#0B60D6]" size={20} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-gray-800 block truncate">Progress Report</span>
                      <span className="text-xs text-gray-500 block">Download progress update document</span>
                    </div>
                  </a>
                ) : (
                  <div className="p-3 rounded-lg border border-gray-100 bg-gray-50 text-center text-xs text-gray-400">
                    No progress report generated yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingClientDetailsView;
